<template>
  <div>
    <h1 class="heading-1 mb-24">{{ i18n(translations.title) }}</h1>
    <p class="body-1 mb-32">{{ i18n(translations.description) }}</p>

    <div class="space-y-24">
      <!-- Business Details Section -->
      <div>
        <div class="mb-16 flex items-center justify-between">
          <h2 class="heading-2">
            {{ i18n(translations.businessDetailsTitle) }}
          </h2>
          <RevButton
            :icon="IconEdit"
            size="small"
            :to="{ name: ROUTE_NAMES.KYB.STEPS.STEP_1 }"
            variant="secondary"
            with-icon
          >
            {{ i18n(translations.businessDetailsEditButton) }}
          </RevButton>
        </div>

        <div class="space-y-16">
          <div class="grid grid-cols-4 gap-16">
            <p class="text-static-default-mid body-1 col-span-1">
              {{ i18n(translations.businessDetailsLegalName) }}
            </p>
            <p class="body-1 col-span-3">{{ kybData.business?.legalName }}</p>
          </div>

          <div class="grid grid-cols-4 gap-16">
            <p class="text-static-default-mid body-1 col-span-1">
              {{ i18n(translations.businessDetailsTradeName) }}
            </p>
            <p class="body-1 col-span-3">{{ kybData.business?.tradeName }}</p>
          </div>

          <div class="grid grid-cols-4 gap-16">
            <p class="text-static-default-mid body-1 col-span-1">
              {{ i18n(translations.businessDetailsCountryOfRegistration) }}
            </p>
            <p class="body-1 col-span-3">
              {{ i18n.country(kybData.business.countryOfRegistration) }}
            </p>
          </div>

          <div class="grid grid-cols-4 gap-16">
            <p class="text-static-default-mid body-1 col-span-1">
              {{ i18n(translations.businessDetailsRegistrationType) }}
            </p>
            <p class="body-1 col-span-3">
              {{ kybData.business?.registrationType }}
            </p>
          </div>

          <div class="grid grid-cols-4 gap-16">
            <p class="text-static-default-mid body-1 col-span-1">
              {{ i18n(translations.businessDetailsRegistrationNumber) }}
            </p>
            <p class="body-1 col-span-3">
              {{ kybData.business?.registrationNumber }}
            </p>
          </div>

          <div class="grid grid-cols-4 gap-16">
            <p class="text-static-default-mid body-1 col-span-1">
              {{ i18n(translations.businessDetailsIncorporationDate) }}
            </p>
            <p class="body-1 col-span-3">
              {{ formatDate(kybData.business?.incorporationDate) }}
            </p>
          </div>
        </div>

        <div class="mt-16 grid grid-cols-4 gap-16">
          <p class="text-static-default-mid body-1 col-span-1">
            {{ i18n(translations.businessDetailsHeadquartersAddress) }}
          </p>
          <p class="body-1 col-span-3">
            {{ formatAddress(kybData.business?.headquartersAddress) }}
          </p>
        </div>
        <div class="mt-16 grid grid-cols-4 gap-16">
          <p class="text-static-default-mid body-1 col-span-1">
            {{ i18n(translations.articleOfIncorporation) }}
          </p>
          <p class="body-1 col-span-3 flex items-center gap-2">
            <IconAttachment class="mr-2" />
            {{ kybData.business?.articleOfIncorporation }}
          </p>
        </div>
        <div class="mt-16 grid grid-cols-4 gap-16">
          <p class="text-static-default-mid body-1 col-span-1">
            {{ i18n(translations.businessDetailsInvestedCapitalCurrency) }}
          </p>
          <p class="body-1 col-span-3">
            {{ kybData.business?.investedCapital.currency }}
          </p>
        </div>
        <div class="mt-16 grid grid-cols-4 gap-16">
          <p class="text-static-default-mid body-1 col-span-1">
            {{ i18n(translations.businessDetailsInvestedCapital) }}
          </p>
          <p class="body-1 col-span-3">
            {{ kybData.business?.investedCapital.amount }}
          </p>
        </div>
      </div>

      <!-- Account Requester Section -->
      <div>
        <div class="mb-16 flex items-center justify-between">
          <h2 class="heading-2">
            {{ i18n(translations.accountRequesterTitle) }}
          </h2>
          <RevButton
            :icon="IconEdit"
            size="small"
            :to="{ name: ROUTE_NAMES.KYB.STEPS.STEP_2 }"
            variant="secondary"
            with-icon
          >
            {{ i18n(translations.accountRequesterEditButton) }}
          </RevButton>
        </div>

        <StakeholderInfo :stakeholder="kybData.accountRequester" />
      </div>

      <!-- Legal Representative Section -->
      <div>
        <div class="mb-16 flex items-center justify-between">
          <h2 class="heading-2">
            {{ i18n(translations.legalRepresentativeTitle) }}
          </h2>
          <RevButton
            :icon="IconEdit"
            size="small"
            :to="{ name: ROUTE_NAMES.KYB.STEPS.STEP_3 }"
            variant="secondary"
            with-icon
          >
            {{ i18n(translations.legalRepresentativeEditButton) }}
          </RevButton>
        </div>

        <template v-if="kybData.legalRepresentative?.sameAsAccountRequester">
          <div
            class="bg-surface-default-mid rounded-lg flex items-center gap-8 p-16"
          >
            <IconCheckInCircleFilled color="green" />
            <p class="body-1">
              {{ i18n(translations.legalRepresentativeSameAsAccountRequester) }}
            </p>
          </div>
        </template>

        <template v-else>
          <StakeholderInfo :stakeholder="kybData.legalRepresentative" />
        </template>
      </div>

      <!-- Beneficial Owner Section -->
      <div>
        <div class="mb-16 flex items-center justify-between">
          <h2 class="heading-2">
            {{ i18n(translations.beneficialOwnerPrimaryOwner) }}
          </h2>
          <RevButton
            :icon="IconEdit"
            size="small"
            :to="{ name: ROUTE_NAMES.KYB.STEPS.STEP_4 }"
            variant="secondary"
            with-icon
          >
            {{ i18n(translations.beneficialOwnerEditButton) }}
          </RevButton>
        </div>

        <StakeholderInfo :stakeholder="kybData.beneficialOwner?.primary" />
      </div>

      <div
        v-for="secondaryBeneficialOwner in kybData.beneficialOwner?.secondary"
        :key="secondaryBeneficialOwner.id!"
      >
        <div class="mb-16 flex items-center justify-between">
          <h2 class="heading-2">
            {{ i18n(translations.otherBeneficialOwnerOwnerDetails) }}
          </h2>
          <RevButton
            :icon="IconEdit"
            size="small"
            :to="{ name: ROUTE_NAMES.KYB.STEPS.STEP_4 }"
            variant="secondary"
            with-icon
          >
            {{ i18n(translations.beneficialOwnerEditButton) }}
          </RevButton>
        </div>

        <StakeholderInfo :stakeholder="secondaryBeneficialOwner" />
      </div>

      <!-- Letter of Authorization Section -->
      <div>
        <div class="mb-16 flex items-center justify-between">
          <h2 class="heading-2">
            {{ i18n(translations.letterOfAuthorizationTitle) }}
          </h2>
          <RevButton
            :icon="IconEdit"
            size="small"
            :to="{ name: ROUTE_NAMES.KYB.STEPS.STEP_5 }"
            variant="secondary"
            with-icon
          >
            {{ i18n(translations.letterOfAuthorizationEditButton) }}
          </RevButton>
        </div>

        <p class="flex items-center gap-2">
          <IconAttachment class="mr-2" /> {{ kybData.letterOfAuthorization }}
        </p>
      </div>
    </div>

    <div
      class="mx-auto mt-48 flex w-[328px] items-center justify-center gap-16"
    >
      <RevButton class="w-full" variant="primary" @click="handleSubmit">
        {{ i18n(translations.submitButton) }}
      </RevButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { IconAttachment } from '@ds/icons/IconAttachment'
import { IconCheckInCircleFilled } from '@ds/icons/IconCheckInCircleFilled'
import { IconEdit } from '@ds/icons/IconEdit'

import { ROUTE_NAMES } from '~/constants/routes'

import type { KybData } from '../../../Index.types'

import translations from './Review.translations'
import StakeholderInfo from './components/StakeholderInfo.vue'

defineProps<{
  kybData: KybData
}>()

const i18n = useI18n()

const formatDate = (dateString?: string): string => {
  if (!dateString) return ''

  return new Date(dateString).toLocaleDateString()
}

const formatAddress = (address?: {
  addressLine1: string
  addressLine2?: string
  postalCode: string
  city: string
  country: string
}): string => {
  if (!address) return ''

  const parts = [
    address.addressLine1,
    address.addressLine2,
    `${address.postalCode} ${address.city}`,
    address.country,
  ].filter(Boolean)

  return parts.join(', ')
}

const handleSubmit = (): void => {
  // TODO: Implement KYB submission logic
  // eslint-disable-next-line no-console
  console.log('Submitting KYB data...')
}
</script>
